import { useEffect, useState } from 'react'

import { api, ErrorApi, type Cliente, type ResultadoBusqueda } from '../api'
import { NuevoCliente, type Borrador } from './NuevoCliente'

const ETIQUETA_FUENTE: Record<ResultadoBusqueda['fuente'], string> = {
  cliente: 'Cliente',
  web: 'Lead web',
  whatsapp: 'Lead WhatsApp',
}

function fecha(iso: string): string {
  return new Date(iso).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric' })
}

export function BuscarClientes({ alAbrir, inicial }: { alAbrir: (c: Cliente | string) => void; inicial?: string }) {
  // `inicial`: el Resumen abre la busqueda con el nombre de un lead para convertirlo aqui.
  const [q, setQ] = useState(inicial ?? '')
  const [resultados, setResultados] = useState<ResultadoBusqueda[] | null>(null)
  const [buscando, setBuscando] = useState(false)
  const [error, setError] = useState('')
  const [borrador, setBorrador] = useState<Borrador | null>(null)

  useEffect(() => {
    const texto = q.trim()
    if (texto.length < 2) {
      setResultados(null)
      setError('')
      return
    }
    // Espera a que se deje de teclear: una busqueda por letra seria una consulta por letra.
    const temporizador = setTimeout(async () => {
      setBuscando(true)
      try {
        const { resultados } = await api.buscarClientes(texto)
        setResultados(resultados)
        setError('')
      } catch (err) {
        setError(err instanceof ErrorApi ? err.message : 'No se pudo buscar.')
      } finally {
        setBuscando(false)
      }
    }, 300)
    return () => clearTimeout(temporizador)
  }, [q])

  const elegir = (r: ResultadoBusqueda) => {
    if (r.fuente === 'cliente') {
      alAbrir(r.id)
      return
    }
    setBorrador({
      nombre: r.nombre,
      telefono: r.telefono,
      correo: r.correo,
      origen: r.fuente,
      origen_id: r.id,
    })
  }

  return (
    <section>
      <div className="encabezado">
        <div>
          <h1>Clientes</h1>
          <p className="suave">Busca por nombre, cédula o RUC. También aparecen quienes cotizaron por la web o WhatsApp.</p>
        </div>
        <button className="primario" onClick={() => setBorrador({ nombre: q.trim(), origen: 'panel' })}>
          + Nuevo cliente
        </button>
      </div>

      <input
        className="buscador"
        type="search"
        placeholder="Ej.: García, 0990004196001…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoFocus
      />

      {error && <p className="error">{error}</p>}
      {buscando && <p className="suave">Buscando…</p>}

      {resultados && !buscando && resultados.length === 0 && (
        <div className="vacio">
          <p>Nadie coincide con «{q.trim()}».</p>
          <button className="secundario" onClick={() => setBorrador({ nombre: q.trim(), origen: 'panel' })}>
            Crear como cliente nuevo
          </button>
        </div>
      )}

      {resultados && resultados.length > 0 && (
        <div className="tabla-envoltura">
          <table className="tabla">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Cédula / RUC</th>
                <th>Contacto</th>
                <th>Consumo</th>
                <th>Origen</th>
                <th>Fecha</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {resultados.map((r) => (
                <tr key={`${r.fuente}-${r.id}`} onClick={() => elegir(r)}>
                  <td className="fuerte">{r.nombre}</td>
                  <td>{r.identificacion ?? <span className="suave">—</span>}</td>
                  <td>
                    {r.telefono || r.correo || <span className="suave">—</span>}
                    {r.telefono && r.correo && <small className="suave bloque">{r.correo}</small>}
                  </td>
                  <td>{r.consumo_kwh ? `${Math.round(r.consumo_kwh)} kWh/mes` : <span className="suave">—</span>}</td>
                  <td>
                    <span className={`insignia insignia-${r.fuente}`}>{ETIQUETA_FUENTE[r.fuente]}</span>
                  </td>
                  <td className="suave">{fecha(r.fecha)}</td>
                  <td className="accion">{r.fuente === 'cliente' ? 'Abrir →' : 'Convertir →'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {borrador && (
        <NuevoCliente
          borrador={borrador}
          alCerrar={() => setBorrador(null)}
          alCrear={(cliente) => {
            setBorrador(null)
            alAbrir(cliente)
          }}
          alExistir={(id) => {
            setBorrador(null)
            alAbrir(id)
          }}
        />
      )}
    </section>
  )
}
